import React, { useState, useEffect } from "react";
import dayjs from "dayjs";

import { Form, Input, Button, DatePicker, Select, message, Tooltip } from "antd";

import {
  TeamOutlined,
  SolutionOutlined,
  getTwoToneColor,
  EditOutlined,
} from "@ant-design/icons";

import { ChevronLeft, Moon } from "lucide-react";

import useCompanies from "../hooks/useCompanies";
import useUsers from "../hooks/useUsers";
import useGroups from "../hooks/useGroups";

import CoordinatorFormModal from "./CoordinatorFormModal.jsx";
import CompanyDetailCard from "./CompanyDetailCard.jsx";

const { RangePicker } = DatePicker;

function GroupForm({ onSetList, groupToEdit }) {
  const { getCompanies, companies, getCompanyDetail, companyDetail, loading: companyLoading } = useCompanies();
  const { getUsers, users } = useUsers();
  const { createGroup, updateGroup, loading } = useGroups();

  const [coordinatorDisabled, setCoordinatorDisabled] = useState(true);
  const [params, setParams] = useState({});
  const [nights, setNights] = useState(0);
  const [selectedCoordinator, setSelectedCoordinator] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  const [form] = Form.useForm();

  useEffect(() => {
    const initializeForm = async () => {
      await getCompanies();

      if (groupToEdit) {
        // Set date range state first
        const checkInDate = dayjs(groupToEdit.checkInDate);
        const checkOutDate = dayjs(groupToEdit.checkOutDate);
        setDateRange([checkInDate, checkOutDate]);
        
        // Calculate nights for initial value
        setNights(checkOutDate.diff(checkInDate, "day"));

        // Set form values with a small delay to ensure form is ready
        setTimeout(() => {
          form.setFieldsValue({
            company: {
              value: groupToEdit.company.id,
              label: groupToEdit.company.name,
            },
            coordinator: groupToEdit.coordinator?.id,
            dateRange: [checkInDate, checkOutDate],
            observation: groupToEdit.observation,
            entryCoordinatorName: groupToEdit.entryCoordinatorName,
            entryNumber: groupToEdit.entryNumber,
            entryGroupObservation: groupToEdit.entryGroupObservation,
          });
        }, 100);

        setParams({ company_id: groupToEdit.company.id });
        setCoordinatorDisabled(false);
        setSelectedCoordinator(groupToEdit.coordinator?.id || null);

        // Load company detail for initial value
        await getCompanyDetail(groupToEdit.company.id);
      }
    };

    initializeForm();
  }, [groupToEdit]);

  const enableCoordinator = async (company) => {
    if (company) {
      setParams({ company_id: company.value });
      setCoordinatorDisabled(false);
      // Load company detail when a company is selected
      await getCompanyDetail(company.value);
    } else {
      setCoordinatorDisabled(true);
    }
  };

  useEffect(() => {
    getUsers(params);
  }, [params]);

  const handleEditCoordinator = () => {
    const coordinatorId = selectedCoordinator || form.getFieldValue("coordinator");
    if (!coordinatorId) {
      message.warning("Por favor selecciona un coordinador primero");
      return;
    }
    
    const baseUrl = process.env.REACT_APP_API_URL;
    const editUrl = `${baseUrl}admin/users/customuser/${coordinatorId}/change/`;
    window.open(editUrl, '_blank');
  };

  const onFinish = async (values) => {
    const { dateRange } = values;

    const [checkInDate, checkOutDate] = dateRange.map((date) =>
      dayjs(date).startOf("day").format("YYYY-MM-DD")
    );

    const formData = {
      ...values,
      checkInDate,
      checkOutDate,
      company: values.company.value,
      id: groupToEdit?.id,
    };

    delete formData.dateRange;

    if (groupToEdit) {
      await updateGroup(formData);
    } else {
      await createGroup(formData);
    }

    onSetList();
  };

  const onFinishFailed = (errorInfo) => {
    message.warning("Formulario inválido");
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates); // Update local state
    
    if (dates && dates.length === 2) {
      const [startDate, endDate] = dates;

      if (endDate) {
        const nightCount = endDate.diff(startDate, "day");
        setNights(nightCount);
      }

      // Make sure form gets updated with the new values
      form.setFieldsValue({ dateRange: dates });
    } else {
      setNights(0);
      form.setFieldsValue({ dateRange: null });
    }
  };

  return (
    <>
      <div className="flex items-center mb-3">
        <Button
          onClick={() => {
            form.resetFields();
            onSetList();
          }}
          type="text"
          icon={<ChevronLeft size={16} />}
          className="me-2"
        >
          Volver
        </Button>

        <div className="flex justify-center items-center col-span-2">
          <span className="font-semibold text-lg text-center ">
            {groupToEdit ? "Edición de grupo" : "Creación de grupo"}
          </span>
        </div>
      </div>

      {/* Company Detail Card */}
      <CompanyDetailCard 
        companyDetail={companyDetail} 
        loading={companyLoading.detail} 
      />

      <div className="w-full flex items-center min-h-[30rem] flex-col">
        <Form
          form={form}
          name="groupForm"
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          className="w-1/2"
        >
          <Form.Item
            label="Empresa"
            name="company"
            rules={[
              {
                required: true,
                message: "Por favor seleccione una compañía.",
              },
            ]}
          >
            <Select
              showSearch
              labelInValue
              optionFilterProp="label"
              options={companies.results?.map((company) => ({
                label: company.name,
                value: company.id,
              }))}
              onSelect={(company) => {
                enableCoordinator(company);
              }}
            />
          </Form.Item>

          <div className="flex w-full items-end gap-2">
            <Form.Item name="coordinator" className="flex-1">
              <Select
                disabled={coordinatorDisabled}
                options={users}
                className="min-w-[20rem]"
                fieldNames={{ label: "firstName", value: "id" }}
                optionRender={(option) =>
                  `${option.data.firstName} ${option.data.lastName}`
                }
                labelRender={(label) => {
                  if (!label) return "";

                  const user = users.find((u) => u.id === label.value);

                  return `${user?.firstName} ${user?.lastName}`;
                }}
                placeholder="Número de coordinador"
                onChange={(value) => {
                  setSelectedCoordinator(value);
                }}
              />
            </Form.Item>

            <Tooltip title="Editar coordinador">
              <Button
                type="default"
                icon={<EditOutlined />}
                onClick={handleEditCoordinator}
                disabled={!selectedCoordinator}
                className="mb-6"
              />
            </Tooltip>

            <CoordinatorFormModal
              company={form.getFieldValue("company")}
              onCreate={(coordinator) => {
                getUsers(params);

                form.setFieldsValue({
                  coordinator: coordinator.id,
                });
                setSelectedCoordinator(coordinator.id);
              }}
              disabled={!form.getFieldValue("company")}
            />
          </div>

          <Form.Item
            label="Rango de Fechas"
            name="dateRange"
            rules={[
              {
                required: true,
                message: "Por favor seleccione el rango de fechas",
              },
            ]}
          >
            <div className="flex items-center">
              <RangePicker
                placeholder={["Fecha de entrada", "Fecha de salida"]}
                className="flex-grow"
                value={dateRange}
                onChange={handleDateRangeChange}
                onCalendarChange={handleDateRangeChange}
              />
              {nights > 0 && (
                <div className="ml-3 flex items-center text-gray-500">
                  <Moon size={16} className="mr-1" />
                  <span>
                    {nights} {nights === 1 ? "noche" : "noches"}
                  </span>
                </div>
              )}
            </div>
          </Form.Item>

          <Form.Item label="Observación" name="observation">
            <Input.TextArea placeholder="Ingrese una observación" />
          </Form.Item>

          <Form.Item
            label="Nombre completo Rebotero"
            name="entryCoordinatorName"
          >
            <Input placeholder="Nombre y apellido" />
          </Form.Item>

          <Form.Item
            label="Teléfono Rebotero"
            name="entryNumber"
            rules={[
              {
                required: false,
                message: "Por favor ingrese el número del rebotero",
              },
            ]}
          >
            <Input placeholder="Teléfono Rebotero" />
          </Form.Item>

          <Form.Item
            label="Observación de Rebotero"
            name="entryGroupObservation"
          >
            <Input.TextArea placeholder="Ingrese una observación del rebotero" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading.post}>
              {groupToEdit ? "Actualizar" : "Crear"} Grupo
            </Button>
          </Form.Item>
        </Form>
      </div>
    </>
  );
}

export default GroupForm;
